import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { bakeHtml, downloadAsFile } from '@/lib/export-html';
import { exportToPdf } from '@/lib/export-pdf';
import { exportProjectZip } from '@/lib/export-zip';
import { useApp } from '@/store/useApp';

export function ExportMenu() {
  const project = useApp((s) =>
    s.activeProjectId ? s.projects.find((p) => p.id === s.activeProjectId) ?? null : null,
  );
  const artifactHtml = useApp((s) => s.artifactHtml);
  const edits = useApp((s) => s.edits);
  const tweakValues = useApp((s) => s.tweakValues);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const r = buttonRef.current?.getBoundingClientRect();
    if (r) setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    function onAway(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      if (buttonRef.current?.contains(target)) return;
      const menu = document.querySelector('[data-cdc-export-menu]');
      if (menu && menu.contains(target)) return;
      setOpen(false);
    }
    window.addEventListener('click', onAway);
    return () => window.removeEventListener('click', onAway);
  }, [open]);

  const canExport = Boolean(project && artifactHtml);

  function handleHtml() {
    if (!project || !artifactHtml) return;
    const baked = bakeHtml({
      html: artifactHtml,
      edits: edits.map((e) => ({ selector: e.selector, newText: e.newText })),
      tweaks: tweakValues.map((t) => ({ variable: t.variable, value: t.value })),
    });
    const safeName = (project.name || 'design').toLowerCase().replace(/[^a-z0-9-_]+/g, '-');
    downloadAsFile(`${safeName}.html`, baked);
    setOpen(false);
  }

  function handlePdf() {
    if (!project || !artifactHtml) return;
    if (!window.confirm('PDF export uses the browser print dialog. Interactive elements will not carry over. Continue?')) {
      return;
    }
    exportToPdf({
      html: artifactHtml,
      edits: edits.map((e) => ({ selector: e.selector, newText: e.newText })),
      tweaks: tweakValues.map((t) => ({ variable: t.variable, value: t.value })),
    });
    setOpen(false);
  }

  async function handleZip() {
    if (!project) return;
    const blob = await exportProjectZip(project.id);
    const safeName = (project.name || 'design').toLowerCase().replace(/[^a-z0-9-_]+/g, '-');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}.cdczip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    setOpen(false);
  }

  return (
    <>
      <Button
        ref={buttonRef}
        size="sm"
        variant="secondary"
        onClick={() => setOpen((o) => !o)}
        disabled={!canExport}
        title={canExport ? 'Export…' : 'No artifact yet'}
      >
        Export ▾
      </Button>
      {open && menuPos && (
        <div
          role="menu"
          data-cdc-export-menu
          style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 70 }}
          className="w-44 rounded-md border border-neutral-200 bg-white p-1 shadow-lg dark:border-neutral-700 dark:bg-neutral-900"
        >
          <MenuItem onClick={handleHtml}>Standalone HTML</MenuItem>
          <MenuItem onClick={handlePdf}>PDF</MenuItem>
          <MenuItem onClick={() => void handleZip()}>Project zip</MenuItem>
        </div>
      )}
    </>
  );
}

function MenuItem({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center rounded px-2 py-1.5 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
    >
      {children}
    </button>
  );
}
